import React, { useEffect, useState } from "react";
import { Form, Input, Button, message, Card, Spin, Switch, Select, Collapse } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFire, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import appTabConfigApi from "../../../api/appTabConfigApi";
import { SCREEN_OPTIONS, OPEN_MODE_OPTIONS } from "./app-config-options";

// Cấu hình NÚT GIỮA thanh tab app (nút tròn nổi dưới cùng) — đặt chung trang
// "Tiện ích App" cho gọn vì cùng nhóm cấu hình giao diện app.
// App đọc file tĩnh /img/app-tab-config.json; sửa ở đây → ghi lại file đó, app
// nhận ở lần mở sau, KHÔNG cần build lại / OTA.
const LABEL_MAX = 12;
const BADGE_MAX = 6;

function CenterTabConfigForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Giá trị đang gõ — khung xem trước + ẩn/hiện ô theo cách mở
    const label = Form.useWatch("label", form);
    const badgeText = Form.useWatch("badgeText", form);
    const badgeEnabled = Form.useWatch("badgeEnabled", form);
    const openMode = Form.useWatch("openMode", form);
    const isScreenMode = openMode === "screen";

    useEffect(() => {
        (async () => {
            try {
                const res = await appTabConfigApi.getConfig();
                const c = res.data?.data?.centerTab || {};
                form.setFieldsValue({
                    label: c.label || "",
                    badgeText: c.badgeText || "",
                    badgeEnabled: c.badgeEnabled !== false,
                    openMode: c.openMode || "screen",
                    screenName: c.screenName || "CommunityFeed",
                    link: c.link || "",
                    injectUserId: c.injectUserId === true,
                });
            } catch {
                message.error("Không tải được cấu hình nút giữa");
            } finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onFinish = async (values) => {
        setSaving(true);
        try {
            await appTabConfigApi.updateConfig({ centerTab: values });
            message.success("Đã lưu. App sẽ nhận ở lần mở tiếp theo.");
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu thất bại");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 text-center">
                <Spin />
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <div className="flex flex-wrap gap-6">
                <div className="flex-1 min-w-[320px]">
                    <div className="flex gap-4 flex-wrap">
                        <Form.Item label="Nhãn dưới nút" name="label" className="flex-1 min-w-[160px]"
                            extra={`Tối đa ${LABEL_MAX} ký tự — ô tab rất hẹp`}>
                            <Input maxLength={LABEL_MAX} showCount placeholder="Deal" />
                        </Form.Item>
                        <Form.Item label="Chữ badge" name="badgeText" className="flex-1 min-w-[160px]"
                            extra={`Tối đa ${BADGE_MAX} ký tự`}>
                            <Input maxLength={BADGE_MAX} showCount placeholder="HOT" />
                        </Form.Item>
                        <Form.Item label="Badge" name="badgeEnabled" valuePropName="checked" className="w-28">
                            <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
                        </Form.Item>
                    </div>

                    <Form.Item label="Cách mở" name="openMode"
                        extra="Giống Tiện ích App: mở màn hình nội bộ hoặc mở link">
                        <Select options={OPEN_MODE_OPTIONS} style={{ maxWidth: 280 }} />
                    </Form.Item>

                    {isScreenMode ? (
                        <Form.Item label="Màn hình app" name="screenName"
                            extra="Mở thẳng màn hình trong app khi nhấn nút giữa.">
                            <Select options={SCREEN_OPTIONS} style={{ maxWidth: 320 }} />
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item label="Link" name="link"
                                rules={[{ required: true, message: "Nhập link http/https" }]}
                                extra="Chèn {userId} để gắn user hiện tại. VD: https://sansale.longhousee.net/deal.html?user_id={userId}">
                                <Input placeholder="https://..." />
                            </Form.Item>
                            <Form.Item label="Gắn user_id vào link" name="injectUserId" valuePropName="checked">
                                <Switch checkedChildren="Có" unCheckedChildren="Không" />
                            </Form.Item>
                        </>
                    )}

                    <Button type="primary" htmlType="submit" loading={saving}
                        icon={<FontAwesomeIcon icon={faFloppyDisk} />}>
                        Lưu nút giữa
                    </Button>
                </div>

                {/* Xem trước — mô phỏng nút tròn nổi + badge như trong app */}
                <div>
                    <div className="mb-2 text-xs text-content-3">Xem trước</div>
                    <div className="inline-flex flex-col items-center rounded-xl border border-line bg-surface px-8 pt-6 pb-3">
                        <div className="relative">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#EE4D2D] to-[#FF8243] text-white shadow-lg ring-4 ring-white">
                                <FontAwesomeIcon icon={faFire} />
                            </div>
                            {badgeEnabled !== false && (
                                <span className="absolute -top-2 -right-3 rounded bg-[#DC2626] px-1.5 text-[8px] font-bold leading-4 text-white ring-2 ring-white">
                                    {(badgeText || "HOT").slice(0, BADGE_MAX)}
                                </span>
                            )}
                        </div>
                        <span className="mt-1 text-[10px] font-semibold text-[#EE4D2D]">
                            {(label || "Deal").slice(0, LABEL_MAX)}
                        </span>
                    </div>
                </div>
            </div>
        </Form>
    );
}

/** Card gập — để trang Tiện ích App vẫn tập trung vào bảng danh sách. */
export default function CenterTabConfigCard() {
    return (
        <Card size="small" className="mb-5" styles={{ body: { padding: 0 } }}>
            <Collapse
                ghost
                items={[
                    {
                        key: "center-tab",
                        label: (
                            <span className="font-semibold">
                                <FontAwesomeIcon icon={faFire} className="text-orange-500 mr-2" />
                                Nút giữa thanh tab App (nhãn, badge, đích đến)
                            </span>
                        ),
                        children: <CenterTabConfigForm />,
                    },
                ]}
            />
        </Card>
    );
}
